import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const normalizeMaxQuantity = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  };

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('spinnin-beans-cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart from localStorage:', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('spinnin-beans-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, variant, quantity = 1) => {
    setCartItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(
        item => item.variantId === variant.id
      );
      const maxQuantity = normalizeMaxQuantity(product.inventoryQuantity);
      const normalizedQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);

      if (existingItemIndex > -1) {
        // Update quantity if item already exists
        const updatedItems = [...prevItems];
        const currentItem = updatedItems[existingItemIndex];
        const nextQuantity = currentItem.quantity + normalizedQuantity;
        const clampedQuantity = currentItem.maxQuantity
          ? Math.min(nextQuantity, currentItem.maxQuantity)
          : nextQuantity;

        updatedItems[existingItemIndex] = {
          ...currentItem,
          quantity: clampedQuantity
        };
        return updatedItems;
      }

      // Add new item
      return [...prevItems, {
        id: product.id,
        variantId: variant.id,
        title: product.title,
        variantTitle: variant.title,
        price: parseFloat(variant.price?.amount || variant.price || 0),
        image: product.images?.[0]?.src || '',
        quantity: maxQuantity ? Math.min(normalizedQuantity, maxQuantity) : normalizedQuantity,
        handle: product.handle,
        maxQuantity,
        // Include Stripe IDs if available
        stripePriceId: variant.stripePriceId,
        stripeProductId: variant.stripeProductId
      }];
    });
  };

  const removeFromCart = (variantId) => {
    setCartItems(prevItems => prevItems.filter(item => item.variantId !== variantId));
  };

  const updateQuantity = (variantId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(variantId);
      return;
    }

    setCartItems(prevItems => prevItems.map(item => {
      if (item.variantId !== variantId) return item;

      const clampedQuantity = item.maxQuantity
        ? Math.min(quantity, item.maxQuantity)
        : quantity;

      return { ...item, quantity: clampedQuantity };
    }));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(prev => !prev);

  const value = {
    cartItems,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    openCart,
    closeCart,
    toggleCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
